import type { NextRouter } from 'next/router';
import { useEffect } from 'react';

import * as D from './data';

export const useModalScroll = (isModalOpen: boolean, router: NextRouter) => {
  useEffect(() => {
    const saveScrollPosition = () => {
      const savedScrollPosition = sessionStorage.getItem('scrollPosition');
      if (!savedScrollPosition) {
        sessionStorage.setItem('scrollPosition', window.pageYOffset.toString());
      }
    };

    if (isModalOpen) {
      saveScrollPosition();
    }

    router.events.on('routeChangeStart', saveScrollPosition);

    return () => {
      router.events.off('routeChangeStart', saveScrollPosition);
    };
  }, [isModalOpen, router.events]);

  useEffect(() => {
    const restoreScrollPosition = () => {
      const savedPosition = sessionStorage.getItem('scrollPosition');
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition, 10));
      }
    };

    router.events.on('routeChangeComplete', restoreScrollPosition);

    return () => {
      router.events.off('routeChangeComplete', restoreScrollPosition);
    };
  }, [router.events]);
};

export const useModalHistory = (
  isModalOpen: boolean,
  close: () => void,
  router: NextRouter,
) => {
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    window.history.pushState({ modal: true }, '');

    const handlePopState = async () => {
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
      await router.push('/campaign', undefined, { shallow: true });
      close();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen, close, router]);

  const handleClose = async () => {
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    if (!savedScrollPosition) {
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    }

    await router.push('/campaign', undefined, { shallow: true });
    close();
  };

  return { handleClose };
};

export const getDonationCertificateData = (
  contributions: number,
): D.DonationCertificatePathData => {
  const certificateData = D.DONATION_CERTIFICATE_PATH_DATA.find(
    (data) =>
      contributions >= data.minContributions &&
      (!data.maxContributions || contributions < data.maxContributions),
  );

  return certificateData || D.DONATION_CERTIFICATE_PATH_DATA[0];
};

export const getUserIdFromStorage = () => {
  const storageData = localStorage.getItem('github-user-data');
  if (!storageData) {
    return null;
  }

  try {
    const { data } = JSON.parse(storageData);
    return data?.gitHubUserId || null;
  } catch {
    return null;
  }
};
